// The Providers card, the Models Families sub-tab (SCR-574, SCR-589): one row per provider of the org's
// global allow-list with its name, an on/off switch, "N of M models allowed", how many some
// installation can call, and Edit, which opens the provider modal (SCR-575). At an installation's
// scope (the billing admin) the list is the org's: shown read-only, "set for the whole org".
import type { HelpId } from "../../primitives/help";
import { Card, Chip } from "../../primitives/page";
import { Empty } from "../../primitives/states";
import type { ProviderRow } from "./provider-model";

const HELP: HelpId = "settings.models.allowed_models";

export interface ProvidersSectionProps {
  rows: readonly ProviderRow[];
  /** False at an installation's scope: the list is set for the whole org. */
  editable: boolean;
  /** Why the controls are disabled (read-only proxy, a save running); null when they are not. */
  disabledNote: string | null;
  onEdit(provider: string): void;
  onToggle(provider: string, enabled: boolean): void;
  /** Moves to the org default, where the list is edited (the billing admin's installation scope). */
  onOpenOrg?: (() => void) | undefined;
}

export function ProvidersSection({ rows, editable, disabledNote, onEdit, onToggle, onOpenOrg }: ProvidersSectionProps) {
  const disabled = disabledNote !== null;
  return (
    <Card title="Providers" help={HELP}>
      <p class="card-note">Which models the organisation allows, per provider. Every agent is offered only these.</p>
      {!editable && (
        <p class="field-hint">
          Set for the whole org.{" "}
          {onOpenOrg && (
            <button type="button" class="button-link" onClick={onOpenOrg}>
              Edit the org default
            </button>
          )}
        </p>
      )}
      {editable && disabledNote && <p class="field-hint">{disabledNote}</p>}
      {rows.length === 0 ? (
        <Empty message="The proxy has not reported its models yet. Providers appear once it has discovered what AWS lets it call." />
      ) : (
        <ul class="provider-rows">
          {rows.map((row) => (
            <li key={row.provider} class="provider-row">
              <div class="provider-row-name">
                <strong>{row.label}</strong>
                <span class="cell-sub">
                  {row.summary}
                  {row.total > 0 && ` · ${row.invokable} can be called`}
                </span>
              </div>
              {row.inherited && <Chip title="This provider has no entry of its own and follows the one for every other provider.">follows every provider</Chip>}
              {editable ? (
                <>
                  {/* help-exempt: the section's title carries the help button. */}
                  <label class="provider-row-switch">
                    <input type="checkbox" role="switch" checked={row.enabled} disabled={disabled} onChange={(event) => onToggle(row.provider, event.currentTarget.checked)} />
                    <span>{row.enabled ? "On" : "Off"}</span>
                    <span class="visually-hidden"> {row.label}</span>
                  </label>
                  <button type="button" class="button button-secondary button-small" aria-haspopup="dialog" aria-label={`Edit ${row.label} models`} disabled={disabled} onClick={() => onEdit(row.provider)}>
                    Edit
                  </button>
                </>
              ) : (
                <Chip tone={row.enabled ? "green" : "grey"}>{row.enabled ? "On" : "Off"}</Chip>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
